import React from "react";
import {
    BooleanRadioButton,
    BooleanRadioButtonGroup,
    RadioButton,
    RadioButtonGroup
} from "@octopusdeploy/design-system-components";
import {HostedInstanceResource} from "../../../client/resources/hostedInstanceResource";
import {BusyState} from "../../../components/BusyFromPromise/BusyFromPromise";
import {DataBaseComponent, DataBaseComponentState, Errors} from "../../../components/DataBaseComponent";
import {RunSqlRequest, SqlOutputFormat} from "../../../client/resources/runSqlRequest";
import SaveDialogLayout from "../../../components/DialogLayout/SaveDialogLayout";
import {Callout} from "@octopusdeploy/design-system-components";
import CommandTimeoutSelect from "shared/CommandTimeoutSelect";
import {Text} from "components/form";
import repository from "../../../client/repository";
import {getRememberedJustification, rememberJustification} from "../../../client/rememberedJustification";

interface RunSqlForCloudPortalProps {
    errors: Errors;
    busy: BusyState;
    instance: HostedInstanceResource;
    onSaveClick(backgroundTaskId: string): Promise<boolean>;
}

interface RunSqlForCloudPortalDialogState extends DataBaseComponentState {
    runSqlRequest: Partial<RunSqlRequest>;
}

export class RunSqlForCloudPortalDialog extends DataBaseComponent<RunSqlForCloudPortalProps, RunSqlForCloudPortalDialogState> {
    constructor(props: RunSqlForCloudPortalProps) {
        super(props);
        this.state = {
            runSqlRequest: {
                CommandTimeout: "0:05:00",
                RunSqlDuringOutageWindow: false,
                IsQuery: true,
                ExecutionPlanOptions: "None",
                OutputFormat: "Json",
                Permission: getRememberedJustification()
            }
        }
    }

    render() {
        const request = this.state.runSqlRequest;

        const showEstimatedPlanCallout = this.state.runSqlRequest.ExecutionPlanOptions === "EstimatedPlanOnly";

        return <SaveDialogLayout title={"Run SQL for Cloud Portal"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 saveButtonLabel={"Run"}
                                 cancelButtonLabel={"Cancel"}
                                 onSaveClick={() => this.runSql(request)}>

            <Callout type="warning" title="Warning">
                <li>Ensure the query will not cause outage or <strong>performance</strong> issues.</li>
            </Callout>

            <BooleanRadioButtonGroup title="Execution Mode" value={this.state.runSqlRequest.IsQuery} onChange={IsQuery => this.updateRunSqlRequest({IsQuery})}>
                <BooleanRadioButton value={true} label="Query (SELECT only — read-only enforced)" />
                <BooleanRadioButton disabled={true} value={false} label="Command (Update/Insert/Delete) — not permitted for the Cloud Portal database" />
            </BooleanRadioButtonGroup>

            <RadioButtonGroup title="Output Format" value={this.state.runSqlRequest.OutputFormat} onChange={(OutputFormat: SqlOutputFormat) => this.updateRunSqlRequest({OutputFormat})}>
                <RadioButton value="Json" label="Json" />
                <RadioButton value="Csv" label="Csv" />
            </RadioButtonGroup>

            <CommandTimeoutSelect value={this.state.runSqlRequest.CommandTimeout}
                                  onChange={CommandTimeout => this.updateRunSqlRequest({CommandTimeout})}
            />

            <Text label="Permission"
                  hintText="Link to email, support ticket, etc."
                  value={this.state.runSqlRequest.Permission}
                  onChange={Permission => this.updateRunSqlRequest({Permission})}
                  autoFocus={true}
                  multiLine={true}
                  rows={2}
            />

            <Callout type="warning" title="Warning">
                <li>Be careful when including sensitive values in your query, any SQL you write will be stored as plaintext.</li>
            </Callout>
            
            <Text label="SQL"
                  hintText="SQL"
                  value={this.state.runSqlRequest.Sql}
                  onChange={Sql => this.updateRunSqlRequest({Sql})}
                  multiLine={true}
                  rows={10}
            />

            <RadioButtonGroup title="Execution Plan" value={this.state.runSqlRequest.ExecutionPlanOptions} onChange={ExecutionPlanOptions => this.updateRunSqlRequest({ExecutionPlanOptions})}>
                <RadioButton value="None" label="None" />
                <RadioButton value="IncludeActualPlan" label="Include Actual" />
                <RadioButton value="EstimatedPlanOnly" label="Estimated Plan Only" />
            </RadioButtonGroup>

            {showEstimatedPlanCallout && <Callout type="information" title="No query results will be returned." />}

        </SaveDialogLayout>;
    }

    private updateRunSqlRequest(update: Partial<RunSqlRequest>) {
        return this.setState(prevState => ({runSqlRequest: {...prevState.runSqlRequest, ...update}}));
    }

    private runSql(request: Partial<RunSqlRequest>) {
        return this.doBusyTask(async () => {
            const backgroundTaskId  = await repository.Administration.runSql(request as RunSqlRequest) as string;
            rememberJustification(request.Permission);
            await this.props.onSaveClick(backgroundTaskId);
            return true;
        })
    }
}
